<template>
  <div>
    <v-toolbar color class="mb-1">
      <v-toolbar-title :style="{ color: getOutlineColor }"
        >Invoices</v-toolbar-title
      >
      <v-spacer></v-spacer>
      <v-text-field
        v-model="searchInvoices"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
      ></v-text-field>
      <v-btn color="#006064" dark x-small rounded @click="openMarkPaidDialog()"
        ><v-icon x-small>mdi-check</v-icon>Mark Paid</v-btn
      >
      <v-icon @click="deleteSelectedInvoices()" color="red">mdi-delete</v-icon>
    </v-toolbar>

    <v-list two-line v-if="invoices">
      <v-list-item>
        <v-list-item-content
          ><v-data-table
            :headers="invoiceHeaders"
            :items="invoices"
            :items-per-page="7"
            class="elevation-1 mt-4"
            dense
            :search="searchInvoices"
          >
            <template v-slot:item.selection="{ item }">
              <v-checkbox
                class="shrink"
                :value="item"
                v-model="selectedInvoicesForPayment"
              ></v-checkbox>
              <v-icon
                :color="getOutlineColor"
                @click="editSelectedInvoice(item)"
                >mdi-pencil</v-icon
              >
            </template>

            <template v-slot:item.client.name="{ item }">
              <v-card-subtitle>
                {{ item.client.name
                }}{{ item.case ? " [" + item.case.title + "]" : "" }}
              </v-card-subtitle>
            </template>

            <template v-slot:item.paid="{ item }">
              <v-btn
                width="100"
                height="23"
                x-small
                :color="determineColor(item)"
                dark
              >
                {{
                  item.totalPayment == 0
                    ? "Unpaid"
                    : getCurrency + item.totalPayment
                }}
              </v-btn>
            </template>

            <template v-slot:item.due_date="{ item }">
              <div
                v-if="
                  item.calculation &&
                  item.totalPayment >= item.calculation.total
                "
              >
                <v-btn
                  width="100"
                  height="23"
                  x-small
                  :color="determineColor(item)"
                  dark
                >
                  <v-icon x-small>mdi-check</v-icon>Done
                </v-btn>
              </div>
              <div
                v-if="
                  item.calculation && item.totalPayment < item.calculation.total
                "
              >
                <v-btn
                  width="100"
                  height="23"
                  x-small
                  :color="determineColor(item)"
                  dark
                >
                  {{ item.due_date }}
                </v-btn>
              </div>
            </template>

            <template v-slot:item.status="{ item }">
              <div
                v-if="
                  item.calculation &&
                  item.totalPayment >= item.calculation.total
                "
              >
                <v-btn
                  width="100"
                  height="23"
                  x-small
                  :color="determineColor(item)"
                  dark
                >
                  <v-icon x-small>mdi-check</v-icon>Paid
                </v-btn>
              </div>
              <div
                v-if="
                  item.calculation && item.totalPayment < item.calculation.total
                "
              >
                <v-btn
                  width="100"
                  height="23"
                  x-small
                  :color="determineColor(item)"
                  dark
                  v-if="item.calculation"
                >
                  Due {{ getCurrency
                  }}{{ item.calculation.total - item.totalPayment }}
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-list-item-content>
      </v-list-item>
      <v-divider></v-divider>
    </v-list>

    <GlobalDialogSlot
      v-if="markPaidDialog"
      :title="
        'Mark Selected ' +
        (selectedInvoicesForPayment.length == 1 ? ' Invoice' : ' Invoices') +
        ' Paid'
      "
      :dialog="globalDialogSlot"
      @close-dialog="closeDialog"
    >
      <v-card>
        <v-card-title>
          {{ selectedInvoicesForPayment.length }}
          {{
            selectedInvoicesForPayment.length == 1 ? " Invoice" : " Invoices"
          }}
          Selected
        </v-card-title>

        <v-container
          v-for="(invoice, ind) in selectedInvoicesForPayment"
          :key="ind + Math.random()"
        >
          <v-row>
            <v-col cols="4">
              {{ ind + 1 }}) &nbsp; {{ invoice.invoice_number }}
            </v-col>
            <v-col cols="4"
              >{{ invoice.client.name }} [{{
                invoice.client.institution
              }}]</v-col
            >
            <v-col cols="4">
              <v-text-field
                :label="'Amount (' + getCurrency + ')'"
                dense
                @blur="updatePaymentAmount(ind)"
                :value="
                  invoice.newPayment
                    ? invoice.newPayment
                    : Number(invoice.calculation.total - invoice.totalPayment)
                "
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="5">
              <DatePickerWithMenu
                label="Payment Date"
                :default="dateInvoicesPaid"
                @date-selected="getSelectedPaymentDate"
              ></DatePickerWithMenu>
            </v-col>
            <v-col cols="2"></v-col>
            <v-col cols="5">
              <v-autocomplete
                v-model="paymentMethod"
                :items="paymentMethods"
                label="Payment Method"
              >
              </v-autocomplete>
            </v-col>
          </v-row>
        </v-container>
        <v-card-actions>
          <v-btn
            class="mx-4 my-2"
            :color="getThemeColor"
            dark
            @click="markInvoicePaidNow()"
            >Mark Now</v-btn
          >
        </v-card-actions>
      </v-card>
    </GlobalDialogSlot>
    <GlobalConfirmDeleteDialog
      :item="confirmDeleteTitle"
      @delete-confirmed="takeActionAfterInvoiceDeleteChoice"
    ></GlobalConfirmDeleteDialog>
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import DatePickerWithMenu from "@/components/myLibrary/DatePickerWithMenu.vue";
import GlobalConfirmDeleteDialog from "@/components/myLibrary/GlobalConfirmDeleteDialog.vue";
import GlobalDialogSlot from "@/components/myLibrary/GlobalDialogSlot.vue";

export default {
  props: ["invoices"],
  mounted() {
    this.determineNewPayment();
  },
  components: {
    DatePickerWithMenu,
    GlobalDialogSlot,
    GlobalConfirmDeleteDialog,
  },
  computed: {
    getCurrency() {
      return this.$store.getters.getCurrency;
    },
    getOutlineColor() {
      return this.$store.getters.getOutlineColor;
    },
    getAccessToken() {
      return this.$store.getters.getAccessToken;
    },
    getThemeColor() {
      return this.$store.getters.getThemeColor;
    },
  },
  data: () => ({
    colors: ["warning", "primary", "#1B5E20", "red", "#3E2723"],
    table: null,
    searchInvoices: null,
    selectedInvoicesForPayment: [],
    selectedInvoice: null,
    selectedInvoices: [],
    markPaidDialog: false,
    globalDialogSlot: false,
    dateInvoicesPaid: new Date().toISOString().substr(0, 10),
    paymentMethod: "Cash",
    paymentMethods: [
      "Cash",
      "Checks",
      "Credit Cards",
      "Debit Cards",
      "EFT",
      "Mobile Payments",
    ],
    invoiceHeaders: [
      { text: "Actions", value: "selection" },
      {
        text: "Invoice #",
        value: "invoice_number",
      },
      {
        text: "Date",
        value: "invoice_date",
      },
      {
        text: "Client",
        align: "start",
        value: "client.name",
      },

      {
        text: "Total",
        value: "calculation.total",
      },

      { text: "Payment", value: "paid" },
      {
        text: "Status",
        value: "status",
      },
      {
        text: "Due Date",
        value: "due_date",
      },
    ],
    confirmDeleteTitle: null,
  }),
  methods: {
    determineNewPayment() {
      if (this.invoices.length > 0) {
        this.invoices.forEach((invoice) => {
          if (invoice && invoice.calculation) {
            if (invoice && !invoice.newPayment) {
              invoice.newPayment = Number(
                invoice.calculation.total - invoice.totalPayment
              );
            }
          }
        });
      }
    },
    openMarkPaidDialog() {
      if (this.selectedInvoicesForPayment.length < 1) {
        alert("You Must Select Atleast 1 Item");
        event.preventDefault();
      } else {
        this.markPaidDialog = true;
        this.globalDialogSlot = true;
      }
    },
    updatePaymentAmount(ind) {
      this.selectedInvoicesForPayment[ind]["newPayment"] = event.target.value;
    },
    getSelectedPaymentDate(dateInvoicesPaid) {
      this.dateInvoicesPaid = dateInvoicesPaid;
    },
    editSelectedInvoice(invoice) {
      this.selectedInvoice = invoice;
      this.$emit("edit-invoice", invoice);
      // this.$router.push({
      //   name: "edit-and-print-invoice",
      //   params: { selectedInvoice: invoice },
      // });
    },
    markInvoicePaidNow() {
      this.$store.commit("setLoader", true);
      axios
        .postDataWithToken(
          "/auth/mark-invoices-paid",
          {
            selectedInvoicesForPayment: this.selectedInvoicesForPayment,
            dateInvoicesPaid: this.dateInvoicesPaid,
            paymentMethod: this.paymentMethod,
          },
          this.getAccessToken
        )
        .then((response) => {
          this.$emit("refresh-needed");
          this.closeDialog();
          this.$store.commit("setLoader", false);
        });
    },
    determineColor(invoice) {
      let color = "cyan";
      if (invoice.calculation.total == invoice.totalPayment) {
        color = "#006064";
      } else if (invoice.due_date < axios.getCurrentDate()) {
        color = "red";
      } else if (invoice.due_date >= axios.getCurrentDate()) {
        color = "#01579B";
      }

      return color;
    },
    deleteSelectedInvoices() {
      if (this.selectedInvoicesForPayment.length < 1) {
        alert("You Must Select Atleast 1 Item");
        event.preventDefault();
      } else {
        this.$store.commit("setGlobalConfirmDelete", true);
        this.confirmDeleteTitle = "Selected Invoices";
        this.table = "invoices";
      }
    },
    takeActionAfterInvoiceDeleteChoice(choice) {
      if (choice) {
        if (this.table == "invoices") {
          this.selectedInvoices = [];
          this.selectedInvoicesForPayment.forEach((invoice) => {
            this.selectedInvoices.push(invoice.id);
          });
          this.bulkDeleteItems(this.selectedInvoices);
        }
      } else {
        this.$store.commit("setGlobalConfirmDelete", false);
      }
    },
    bulkDeleteItems(rowIds) {
      this.$store.commit("setLoader", true);
      axios
        .postDataWithToken(
          "/auth/bulk-delete",
          { table: this.table, rowIds },
          this.getAccessToken
        )
        .then((response) => {
          this.$emit("refresh-needed");
          this.$store.commit("setLoader", false);
          this.closeDialog();
        });
    },
    closeDialog() {
      this.selectedInvoicesForPayment = [];
      this.globalDialogSlot = false;
      this.markPaidDialog = false;
      this.confirmDeleteTitle = null;
    },
  },
};
</script>

<style></style>
